import { ComponentType, LazyExoticComponent, lazy } from 'react';

import { reloadForStaleChunkOnce } from '@shared/bootstrap/reloadForStaleChunkOnce';

const RETRY_DELAY_MS = 400;

/**
 * `React.lazy` qui survit à un chunk qui ne se télécharge pas.
 *
 * `React.lazy` mémoïse la promesse : une fois rejetée, elle l'est pour toute la vie de la page —
 * le bouton « Réessayer » de l'ErrorBoundary ne retente donc jamais le chargement, et l'agent
 * reste bloqué sur l'écran d'erreur. D'où deux garde-fous en amont :
 * 1. une seconde chance, qui absorbe le hoquet réseau transitoire (agent en mobilité) ;
 * 2. à défaut, un rechargement de la page, qui repart d'un `index.html` frais donc de hash de
 *    bundle à jour (cas du bundle périmé après déploiement, qu'aucune nouvelle tentative sur la
 *    même URL ne peut résoudre). Si la garde anti-boucle le refuse, l'erreur remonte à
 *    l'ErrorBoundary.
 *
 * Réservé aux composants de route, montés sans props.
 */
export function lazyWithRetry(
    importModule: () => Promise<{ default: ComponentType }>,
): LazyExoticComponent<ComponentType> {
    return lazy(() =>
        importModule().catch(() =>
            wait(RETRY_DELAY_MS)
                .then(importModule)
                .catch((error: unknown) => {
                    reloadForStaleChunkOnce();
                    throw error;
                }),
        ),
    );
}

function wait(delayMs: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, delayMs);
    });
}
